import type { JSX, ReactNode } from 'react';

interface CardProps {
  readonly children: ReactNode;
  readonly className?: string;
}

interface CardHeaderProps {
  readonly title: string;
  readonly hint?: string;
  readonly action?: ReactNode;
}

/**
 * The one surface primitive. Composition over configuration: a card is built
 * from Card.Header and children, not from a dozen boolean props (§7.2).
 */
export function Card({ children, className = '' }: CardProps): JSX.Element {
  return (
    <section
      className={`rounded-[var(--radius-lg)] border border-border bg-surface shadow-sm ${className}`}
    >
      {children}
    </section>
  );
}

function CardHeader({ title, hint, action }: CardHeaderProps): JSX.Element {
  return (
    <header className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
      <div className="flex flex-col gap-0.5">
        <h2 className="type-headline text-text">{title}</h2>
        {hint === undefined ? null : <p className="type-caption text-muted">{hint}</p>}
      </div>
      {action}
    </header>
  );
}

function CardBody({ children, className = '' }: CardProps): JSX.Element {
  return <div className={`px-5 pb-5 ${className}`}>{children}</div>;
}

Card.Header = CardHeader;
Card.Body = CardBody;
